import React, { useMemo } from "react";
import { Nav, TooltipHost } from "@fluentui/react";
import icoifsdocument from "../assets/icoIfsDocument.svg";
import icocheckin from "../assets/icoCheckin.svg";
import workingDocumentIcon from "../assets/workingDocument.svg";
import templateIcon from "../assets/svg/template.svg";

import {
  PAGE_NAME,
  SEARCH_BY_FILE_NAME,
  ONEDRIVE_FILES_LIST,
  DATA_LOAD_STATUS,
  NAVIGATION_PAGE,
  DOCUMENT_LOAD_STATUS,
} from "../redux/constants/metaData";
import NavDrawer from "./NavDrawer";
import { EMPTY_RESULTS } from "../redux/constants/messageTypes";
import { useTheme } from "../theme/OAS365Theme";
import { useDocuments } from "../redux/hooks/useDocuments";
import { useTools } from "../redux/hooks/useTools";
import { useSidebarNav } from "../redux/hooks/useSidebarNav";
import { usePreviewInTeams } from "../redux/hooks/usePreviewInTeams";
import { useOneDriveDocsList } from "../redux/hooks/useOneDriveDocsList";
import { APP_NAV_ITEMS, getCheckInDocumentsNavLabel } from "../routes/routeConfig";
import { useLogin } from "../redux/hooks/useLogin";
import { getSidebarWidth } from "../layout/sidebarLayout";

/** Icon by iconKey from APP_NAV_ITEMS – single place for sidebar icons. */
const NAV_ICONS = {
  workingDocuments: workingDocumentIcon,
  allDocuments: icoifsdocument,
  templates: templateIcon,
  checkInNew: icocheckin,
};

const navigationStyles = {
  root: {
    overflowY: "auto",
    width: 200, //INFE: TO BE UNCOMMENTED
    height: "calc(100vh - 96px)",
  },

  link: {
    textAlign: "start",
    paddingLeft: 7,
    selectors: { "::after": { borderLeft: 0 } },
    color: "",
    ".ms-Nav-compositeLink:hover": {
      color: "black",
    },

    ".ms-Nav-compositeLink:active ,focus-within": {
      color: "black",
    },
  },

  chevronButton: {
    width: 10,
    background: "transparent",
    color: "transparent",
    selectors: { "::after": { borderLeft: 0 } },
  },
};

const SideBarNav = ({
  visibleMobileMenu = false,
  hideOnClick,
  hideSidebarOnMouseOut,
  collapsed = false,
} = {}) => {
  const { onSetDocumentInitialState } = useDocuments();
  const {
    onSelectedPageChange,
    onSearchValueChange,
    onSearchButtonDisable,
    onEmptyResults,
    onRefreshCheckinNew,
    onSearchTypeChange,
    onSearchByChange,
    onRefreshCheckinNewRevision,
    toggleSideBar,
    onDocCheckinTypeChange,
    onResetLangauage,
    clickedOnSameNavItem,
    onOneDriveDataLoad,
    onObjectConnectionDataLoad,
    onCheckInToIFSClicked,
    onClearOneDriveDownloadUrl,
    onOneDriveDocumentDownloadUrlChange,
    onOnlyWhereUserInvolvedChecked,
    onCheckedOutToMe,
    onIfsDocumentLoadChange,
    onFileTypeSelection,
    onOneDriveFileTypeSelection,
    onWorkingDocumentListStatusChange,
    onLoginRefreshPreActionClear,
    onRequestRefreshAllDocuments,
    onRequestRefreshWorkingDocuments,
    onRequestRefreshTemplates,
  } = useTools();

  const { requestRefreshCheckInNewList } = useOneDriveDocsList();

  const theme = useTheme();
  const sidebarWidth = getSidebarWidth(collapsed);
  const navStyles = {
    ...navigationStyles,
    root: {
      ...navigationStyles.root,
      width: sidebarWidth,
      height: collapsed ? "auto" : navigationStyles.root.height,
    },
    link: {
      ...navigationStyles.link,
      fontSize: theme.fontSizes.normal,
      fontFamily: theme.fonts.regular,
      display: "flex",
      alignItems: "center",
      gap: collapsed ? 0 : 6,
      paddingLeft: collapsed ? 0 : navigationStyles.link.paddingLeft,
      paddingRight: collapsed ? 0 : undefined,
      justifyContent: collapsed ? "center" : "flex-start",
      height: collapsed ? 44 : undefined,
      lineHeight: "normal",
      overflow: "hidden",
    },
    // Custom onRenderLink renders icon + label; hide Fluent's duplicate linkText.
    linkText: {
      display: "none",
    },
    compositeLink: {
      width: collapsed ? sidebarWidth : undefined,
      maxWidth: collapsed ? sidebarWidth : undefined,
      boxSizing: "border-box",
      selectors: {
        "&:hover": {
          background: theme.colors.navSelectedBackground ?? "#EEF4EF",
        },
        "&:hover .ms-Nav-link": {
          background: theme.colors.navSelectedBackground ?? "#EEF4EF",
        },
        "&:hover::before": {
          background: "transparent",
          width: 0,
        },
        "&.is-selected": {
          background: theme.colors.navSelectedBackground ?? "#EEF4EF",
          border: collapsed
            ? "none"
            : `1px solid ${theme.colors.navSelectedStroke ?? "#4C8D6A"}`,
        },
        "&.is-selected:hover::before": {
          background: theme.colors.navSelectedStroke ?? "#C1D4C7",
          width: undefined,
        },
      },
    },
  };
  const { selectedKey, navigateToNavKey } = useSidebarNav();
  const { clearPreviewUrl } = usePreviewInTeams();
  const { teamsContext } = useLogin();
  const checkInDocumentsNavLabel = getCheckInDocumentsNavLabel(teamsContext);
  const navGroups = useMemo(
    () => [
      {
        links: APP_NAV_ITEMS.map((item) => ({
          name:
            item.key === NAVIGATION_PAGE.checkinFromTeams
              ? checkInDocumentsNavLabel
              : item.label,
          key: item.key,
        })),
      },
    ],
    [checkInDocumentsNavLabel]
  );

  const sideMenuBar = (isMobile = false) => (
    <Nav
      className={collapsed ? "sidebar-nav--collapsed" : undefined}
      onLinkClick={(ev, item) => {
        const isReselect = selectedKey === item.key;
        switch (item.key) {
          case NAVIGATION_PAGE.home:
            onSelectedPageChange(PAGE_NAME.home);
            clearPreviewUrl();
            onOneDriveDocumentDownloadUrlChange("");
            toggleSideBar(true);
            onSetDocumentInitialState();
            onRefreshCheckinNewRevision(true);
            onRefreshCheckinNew(true);
            onSearchValueChange("");
            onSearchButtonDisable(false);
            onEmptyResults(EMPTY_RESULTS);
            onSearchTypeChange(false);
            onSearchByChange(SEARCH_BY_FILE_NAME);
            onResetLangauage("all");
            clickedOnSameNavItem(true);
            onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onLoad);
            onClearOneDriveDownloadUrl(true);
            onOnlyWhereUserInvolvedChecked("FALSE");
            onCheckedOutToMe("FALSE");
            onFileTypeSelection("");
            onIfsDocumentLoadChange(DATA_LOAD_STATUS.onLoad);
            onLoginRefreshPreActionClear();
            onRequestRefreshWorkingDocuments();
            navigateToNavKey(item.key, { refresh: isReselect });
            break;
          case NAVIGATION_PAGE.allDocuments:
            onSelectedPageChange(PAGE_NAME.allDocuments);
            clearPreviewUrl();
            onOneDriveDocumentDownloadUrlChange("");
            toggleSideBar(true);
            onSetDocumentInitialState();
            onRefreshCheckinNewRevision(true);
            onRefreshCheckinNew(true);
            onSearchValueChange("");
            onSearchButtonDisable(false);
            onEmptyResults(EMPTY_RESULTS);
            onSearchTypeChange(false);
            onSearchByChange(SEARCH_BY_FILE_NAME);
            onResetLangauage("all");
            clickedOnSameNavItem(true);
            onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onLoad);
            onClearOneDriveDownloadUrl(true);
            onOnlyWhereUserInvolvedChecked("FALSE");
            onCheckedOutToMe("FALSE");
            onFileTypeSelection("");
            onIfsDocumentLoadChange(DATA_LOAD_STATUS.onLoad);
            onLoginRefreshPreActionClear();
            if (isReselect) {
              onRequestRefreshAllDocuments();
            }
            onRequestRefreshWorkingDocuments();
            navigateToNavKey(item.key, { refresh: isReselect });
            break;
          case NAVIGATION_PAGE.templates:
            clearPreviewUrl();
            onOneDriveDocumentDownloadUrlChange("");
            toggleSideBar(true);
            onSearchValueChange("");
            onSearchButtonDisable(false);
            onEmptyResults(EMPTY_RESULTS);
            onSearchTypeChange(false);
            onSearchByChange(SEARCH_BY_FILE_NAME);
            clickedOnSameNavItem(true);
            onClearOneDriveDownloadUrl(true);
            onLoginRefreshPreActionClear();
            onRequestRefreshTemplates();
            navigateToNavKey(item.key, { refresh: isReselect });
            break;
          case NAVIGATION_PAGE.checkinFromTeams:
            onSelectedPageChange(PAGE_NAME.checkInFromTeams);
            clearPreviewUrl();
            onOneDriveDocumentDownloadUrlChange("");
            toggleSideBar(true);
            onDocCheckinTypeChange(ONEDRIVE_FILES_LIST);
            onRefreshCheckinNew(true);
            onRefreshCheckinNewRevision(true);
            onSearchValueChange("");
            onSearchButtonDisable(false);
            onEmptyResults(EMPTY_RESULTS);
            onSearchTypeChange(false);
            onResetLangauage("all");
            onOneDriveFileTypeSelection("");
            onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
            onObjectConnectionDataLoad(DATA_LOAD_STATUS.onStart);
            onCheckInToIFSClicked(true);
            onClearOneDriveDownloadUrl(true);
            onOnlyWhereUserInvolvedChecked("FALSE");
            onCheckedOutToMe("FALSE");
            onLoginRefreshPreActionClear();
            requestRefreshCheckInNewList();
            navigateToNavKey(item.key, { refresh: isReselect });
            break;
          default:
            break;
        }
      }}
      groups={navGroups}
      selectedKey={selectedKey}
      styles={navStyles}
      onRenderLink={(props, defaultRender) => {
        const navItem = APP_NAV_ITEMS.find((i) => i.key === props.key);
        const iconSrc = navItem && NAV_ICONS[navItem.iconKey];
        if (navItem && iconSrc) {
          const iconSize =
            navItem.iconKey === "templates" ? 23 : 25;
          const icon = (
            <img
              src={iconSrc}
              width={iconSize}
              height={iconSize}
              alt=""
              aria-hidden
              style={{ flexShrink: 0 }}
            />
          );
          const label = (
            <span
              style={{
                color: theme.colors.sideBarFont,
                fontSize: theme.fontSizes.normal,
                fontFamily: theme.fonts.regular,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {props.name}
            </span>
          );
          if (collapsed) {
            return (
              <TooltipHost content={props.name} id={`nav-${props.key}`}>
                {icon}
              </TooltipHost>
            );
          }
          return (
            <div style={{ width: 190 }}>
              <img
                style={{ verticalAlign: "middle" }}
                src={iconSrc}
                width={iconSize}
                height={iconSize}
                alt={props.name}
              />
              <span
                style={{
                  verticalAlign: "middle",
                  marginLeft: 2,
                  color: theme.colors.sideBarFont,
                  fontSize: theme.fontSizes.normal,
                  fontFamily: theme.fonts.regular,
                }}
              >
                {props.name}
              </span>
            </div>
          );
        }
        return defaultRender(props);
      }}
    />
  );

  return (
    <>
      {visibleMobileMenu && (
        <NavDrawer
          hideSidebarOnMouseOut={hideSidebarOnMouseOut}
          isMenuOpen={hideOnClick}
        >
          {sideMenuBar(true)}
        </NavDrawer>
      )}
      {!visibleMobileMenu && sideMenuBar()}
    </>
  );
};

export default SideBarNav;
